/**
 * Service-scoped CDN hooks — thin wrappers around the 4 per-service endpoints:
 *   GET /api/v3/services/:id/cdn/domains
 *   GET /api/v3/services/:id/cdn/overview
 *   GET /api/v3/services/:id/cdn/analytics
 *   GET /api/v3/services/:id/cdn/business
 *
 * Each hook mirrors the signature and return shape of its org-wide counterpart
 * in domains.ts / analytics.ts so callers can swap them transparently.
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { type Envelope, type DomainListPagination } from './_shared';
import { type CDNDomain } from './domains';
import {
  type CentralQueryResult,
  type DomainUsageRow,
  type ProjectUsageGroup,
  type UsageByProjectResult,
  type UsageRegionsResult,
  type UsageSummaryResult,
} from './analytics';
import type { DomainListQuery } from './domains';

// ---------------------------------------------------------------------------
// Domains  GET /services/:id/cdn/domains
// ---------------------------------------------------------------------------

export interface ServiceCdnDomainList {
  data: CDNDomain[];
  pagination: DomainListPagination;
}

/**
 * Server-paginated domain list scoped to one service. Mirrors
 * `useDomainListPaged` exactly — same params, same return shape.
 */
export function useServiceCdnDomainsPaged(serviceId: string, q: DomainListQuery) {
  return useQuery({
    queryKey: [
      'cdn',
      'service',
      serviceId,
      'domains',
      'paged',
      q.page,
      q.perPage,
      q.search ?? '',
      q.sort ?? '',
      q.order ?? '',
      q.projectId ?? '',
      q.teamId ?? '',
      (q.tags ?? []).join(','),
      (q.statuses ?? []).join(','),
      q.createdFrom ?? '',
      q.createdTo ?? '',
    ],
    enabled: !!serviceId && (q.enabled ?? true),
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: async (): Promise<ServiceCdnDomainList> => {
      const params = new URLSearchParams();
      params.set('page', String(q.page));
      params.set('per_page', String(q.perPage));
      if (q.search) params.set('search', q.search);
      if (q.sort) params.set('sort', q.sort);
      if (q.order) params.set('order', q.order);
      if (q.projectId) params.set('project_id', q.projectId);
      if (q.teamId) params.set('team_id', q.teamId);
      (q.tags ?? []).forEach((t) => params.append('tags', t));
      (q.statuses ?? []).forEach((s) => params.append('status', s));
      if (q.createdFrom) params.set('created_from', q.createdFrom);
      if (q.createdTo) params.set('created_to', q.createdTo);
      const res = await serviceApiV3.get<Envelope<CDNDomain[]>>(
        `/services/${serviceId}/cdn/domains?${params.toString()}`,
      );
      return {
        data: res.data?.data ?? [],
        pagination: res.data?.pagination ?? { page: q.page, per_page: q.perPage, total: 0 },
      };
    },
  });
}

// ---------------------------------------------------------------------------
// Overview  GET /services/:id/cdn/overview
// ---------------------------------------------------------------------------

export interface ServiceCdnOverview {
  service_id: string;
  total: number;
  active: number;
  disabled: number;
}

export function useServiceCdnOverview(serviceId: string) {
  return useQuery({
    queryKey: ['cdn', 'service', serviceId, 'overview'],
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<ServiceCdnOverview | null> => {
      const res = await serviceApiV3.get<Envelope<ServiceCdnOverview>>(
        `/services/${serviceId}/cdn/overview`,
      );
      return res.data?.data ?? null;
    },
  });
}

// ---------------------------------------------------------------------------
// Access mode  GET /services/:id/cdn/access  → { mode: "full" | "report" }
// Lets the console render the read-only surface for a report-only member.
// Bare (non-enveloped) body. Defaults to "full" so a missing/failed probe never
// hides management UI for a legitimately-full member.
// ---------------------------------------------------------------------------

export type CdnAccessMode = 'full' | 'report';

export function useServiceCdnAccess(serviceId: string) {
  return useQuery({
    queryKey: ['cdn', 'service', serviceId, 'access'],
    enabled: !!serviceId,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CdnAccessMode> => {
      const res = await serviceApiV3.get<{ mode?: string }>(`/services/${serviceId}/cdn/access`);
      return res.data?.mode === 'report' ? 'report' : 'full';
    },
  });
}

// ---------------------------------------------------------------------------
// Analytics  GET /services/:id/cdn/analytics
// Mirrors the `useCentralSeries` signature / return shape.
// ---------------------------------------------------------------------------

export interface ServiceCdnAnalyticsParams {
  metric: string;
  from: string;
  to: string;
  granularity?: string;
  groupBy?: 'provider';
  breakdown?: 'code' | 'country';
  fill?: boolean;
  /** Comma-separated domain UUIDs to narrow the family scope (Service Overview picker). */
  domainIds?: string;
}

export function useServiceCdnAnalytics(serviceId: string, params: ServiceCdnAnalyticsParams) {
  const { metric, from, to, granularity, groupBy, breakdown, fill, domainIds } = params;
  return useQuery({
    queryKey: [
      'cdn',
      'service',
      serviceId,
      'analytics',
      metric,
      from,
      to,
      granularity ?? '',
      groupBy ?? 'agg',
      breakdown ?? '',
      domainIds ?? '',
    ],
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<CentralQueryResult | null> => {
      const p = new URLSearchParams({ metric, from, to });
      if (granularity) p.set('granularity', granularity);
      if (groupBy) p.set('group_by', groupBy);
      if (breakdown) p.set('breakdown', breakdown);
      if (fill !== undefined) p.set('fill', String(fill));
      if (domainIds) p.set('domain_ids', domainIds);
      try {
        const res = await serviceApiV3.get<Envelope<CentralQueryResult>>(
          `/services/${serviceId}/cdn/analytics?${p.toString()}`,
        );
        return res.data?.data ?? null;
      } catch {
        return null;
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Business usage  GET /services/:id/cdn/business
// Mirrors `useDomainUsage` return shape.
// ---------------------------------------------------------------------------

export interface ServiceCdnBusinessParams {
  from: string;
  to: string;
  granularity?: string;
}

export function useServiceCdnBusiness(serviceId: string, params: ServiceCdnBusinessParams | null) {
  const { from = '', to = '', granularity } = params ?? {};
  return useQuery({
    queryKey: ['cdn', 'service', serviceId, 'business', from, to, granularity ?? ''],
    enabled: !!serviceId && !!params,
    retry: false,
    queryFn: async (): Promise<DomainUsageRow[]> => {
      const p = new URLSearchParams({ from, to });
      if (granularity) p.set('granularity', granularity);
      try {
        // Service-scoped /cdn/business returns the org-wide envelope shape
        // (`{ data: { granularity, from, to, domains } }`) — same as useDomainUsage
        // and the by-project sibling.
        const res = await serviceApiV3.get<Envelope<{ domains: DomainUsageRow[] }>>(
          `/services/${serviceId}/cdn/business?${p.toString()}`,
        );
        return res.data?.data?.domains ?? [];
      } catch {
        return [];
      }
    },
  });
}

/**
 * Service-scoped business usage grouped by owning project — mirrors the org-wide
 * `useUsageByProject` shape. Unmapped domains land in an "Unassigned" group.
 */
export function useServiceCdnBusinessByProject(
  serviceId: string,
  params: ServiceCdnBusinessParams | null,
) {
  const { from = '', to = '', granularity } = params ?? {};
  return useQuery({
    queryKey: ['cdn', 'service', serviceId, 'business-by-project', from, to, granularity ?? ''],
    enabled: !!serviceId && !!params,
    retry: false,
    queryFn: async (): Promise<ProjectUsageGroup[]> => {
      const p = new URLSearchParams({ from, to });
      if (granularity) p.set('granularity', granularity);
      try {
        const res = await serviceApiV3.get<Envelope<UsageByProjectResult>>(
          `/services/${serviceId}/cdn/business/by-project?${p.toString()}`,
        );
        return res.data?.data?.projects ?? [];
      } catch {
        return [];
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Region split + headline KPIs  GET /services/:id/cdn/{regions,summary}
// Service-view twins of the org useUsageRegions / useUsageSummary — same shapes,
// scoped to one service's CDN domain family, so the service Business Usage page
// shows By-distribution + the KPI strip on its OWN traffic.
// ---------------------------------------------------------------------------

/** Service-scoped billing-basis region split — mirrors `useUsageRegions`. */
export function useServiceCdnRegions(
  serviceId: string,
  range: { from: string; to: string } | null,
) {
  return useQuery({
    queryKey: ['cdn', 'service', serviceId, 'regions', range?.from ?? '', range?.to ?? ''],
    enabled: !!serviceId && !!range,
    retry: false,
    queryFn: async (): Promise<UsageRegionsResult | null> => {
      const p = new URLSearchParams({ from: range!.from, to: range!.to });
      try {
        const res = await serviceApiV3.get<Envelope<UsageRegionsResult>>(
          `/services/${serviceId}/cdn/regions?${p.toString()}`,
        );
        return res.data?.data ?? null;
      } catch {
        return null;
      }
    },
  });
}

/** Service-scoped headline KPIs — mirrors `useUsageSummary`. */
export function useServiceCdnSummary(
  serviceId: string,
  range: { from: string; to: string } | null,
) {
  return useQuery({
    queryKey: ['cdn', 'service', serviceId, 'summary', range?.from ?? '', range?.to ?? ''],
    enabled: !!serviceId && !!range,
    retry: false,
    queryFn: async (): Promise<UsageSummaryResult | null> => {
      const p = new URLSearchParams({ from: range!.from, to: range!.to });
      try {
        const res = await serviceApiV3.get<Envelope<UsageSummaryResult>>(
          `/services/${serviceId}/cdn/summary?${p.toString()}`,
        );
        return res.data?.data ?? null;
      } catch {
        return null;
      }
    },
  });
}
